import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import PageLayout from '../../components/ui/PageLayout';
import Button from '../../components/ui/Button';
import { Plus, Users, TrendingUp, Target, AlertCircle, CheckCircle, DollarSign } from 'lucide-react';

const FunnelBuilder = () => {
  const { user } = useAuth();
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newFunnel, setNewFunnel] = useState({
    name: '',
    description: '',
    status: 'active'
  });

  const stageTypes = [
    { value: 'discovery', label: 'Discovery', color: 'bg-blue-100 text-blue-800', icon: Users },
    { value: 'qualification', label: 'Qualification', color: 'bg-yellow-100 text-yellow-800', icon: Target },
    { value: 'proposal', label: 'Proposal', color: 'bg-purple-100 text-purple-800', icon: TrendingUp },
    { value: 'negotiation', label: 'Negotiation', color: 'bg-orange-100 text-orange-800', icon: DollarSign },
    { value: 'closing', label: 'Closing', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    { value: 'won', label: 'Won', color: 'bg-green-200 text-green-900', icon: CheckCircle },
    { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-800', icon: AlertCircle }
  ];

  useEffect(() => {
    if (user?.id) {
      loadFunnels();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (selectedFunnel?.id) {
      loadFunnelLeads();
    }
  }, [selectedFunnel?.id]);

  const loadFunnels = async () => {
    try {
      setLoading(true);
      const { data, error: funnelError } = await supabase
        ?.from('sales_funnels')
        ?.select('*, funnel_steps(id, name, step_type, order_index)')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (funnelError) {
        console.warn('Error loading funnels:', funnelError?.message);
        setFunnels([]);
      } else {
        setFunnels(data || []);
        if (data?.length > 0) {
          setSelectedFunnel(data?.[0]);
        }
      }
    } catch (err) {
      setError('Failed to load funnels');
    } finally {
      setLoading(false);
    }
  };

  const loadFunnelLeads = async () => {
    if (!selectedFunnel?.id) return;
    try {
      const { data } = await supabase
        ?.from('funnel_leads')
        ?.select('*')
        ?.eq('funnel_id', selectedFunnel?.id)
        ?.limit(100);
      setLeads(data || []);
    } catch (err) {
      console.error('Error loading leads:', err);
    }
  };

  const handleCreateFunnel = async () => {
    if (!newFunnel?.name?.trim()) {
      setError('Funnel name is required');
      return;
    }
    if (!user?.id) return;

    setSaving(true);
    try {
      const { data, error: createError } = await supabase
        ?.from('sales_funnels')
        ?.insert([{
          ...newFunnel,
          user_id: user?.id,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        }])
        ?.select()
        ?.single();

      if (createError) throw createError;

      setFunnels(prev => [data, ...prev]);
      setSelectedFunnel(data);
      setShowCreateModal(false);
      setNewFunnel({ name: '', description: '', status: 'active' });
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to create funnel');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading funnel builder...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Funnel Builder</h1>
          <p className="text-muted-foreground">Build and visualize your sales pipeline stages</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          {funnels?.length > 0 && (
            <select
              value={selectedFunnel?.id || ''}
              onChange={(e) => setSelectedFunnel(funnels?.find(f => f?.id === e?.target?.value))}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            >
              {funnels?.map(f => <option key={f?.id} value={f?.id}>{f?.name}</option>)}
            </select>
          )}
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowCreateModal(true)}>
            New Funnel
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Funnel Stages Visualization */}
      {selectedFunnel ? (
        <div className="space-y-6">
          {/* Funnel Info */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-2">{selectedFunnel?.name}</h2>
            <p className="text-muted-foreground">{selectedFunnel?.description || 'No description'}</p>
            <div className="mt-4 flex items-center space-x-4 text-sm">
              <span className="text-muted-foreground">Total Leads: <strong className="text-foreground">{leads?.length}</strong></span>
              <span className="text-muted-foreground">Converted: <strong className="text-foreground">{leads?.filter(l => l?.is_converted)?.length}</strong></span>
            </div>
          </div>

          {/* Stage Pipeline */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-6">Pipeline Stages</h3>
            <div className="flex flex-wrap gap-4">
              {stageTypes?.map((stage, idx) => {
                const StageIcon = stage?.icon;
                const stageLeads = leads?.filter(l => {
                  if (stage?.value === 'won') return l?.is_converted;
                  if (stage?.value === 'discovery') return (l?.lead_score || 0) < 30;
                  if (stage?.value === 'qualification') return (l?.lead_score || 0) >= 30 && (l?.lead_score || 0) < 50;
                  if (stage?.value === 'proposal') return (l?.lead_score || 0) >= 50 && (l?.lead_score || 0) < 70;
                  if (stage?.value === 'negotiation') return (l?.lead_score || 0) >= 70 && (l?.lead_score || 0) < 85;
                  if (stage?.value === 'closing') return (l?.lead_score || 0) >= 85 && !l?.is_converted;
                  return false;
                });

                return (
                  <div key={idx} className="flex-1 min-w-[140px] bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                    <div className={`inline-flex p-2 rounded-lg ${stage?.color} mb-3`}>
                      <StageIcon className="w-5 h-5" />
                    </div>
                    <div className="font-semibold text-gray-900 text-sm mb-1">{stage?.label}</div>
                    <div className="text-2xl font-bold text-gray-700">{stageLeads?.length}</div>
                    <div className="text-xs text-gray-500">leads</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Target className="w-16 h-16 text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No Funnels Yet</h3>
          <p className="text-muted-foreground mb-6">Create your first funnel to start building your pipeline</p>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowCreateModal(true)}>
            Create Your First Funnel
          </Button>
        </div>
      )}

      {/* Create Funnel Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Create New Funnel</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Funnel Name *</label>
                <input
                  type="text"
                  value={newFunnel?.name}
                  onChange={(e) => setNewFunnel(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="e.g., Main Sales Pipeline"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newFunnel?.description}
                  onChange={(e) => setNewFunnel(prev => ({ ...prev, description: e?.target?.value }))}
                  placeholder="Describe this funnel..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => { setShowCreateModal(false); setError(''); }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFunnel}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Create Funnel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default FunnelBuilder;